import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren
} from 'react';

type McpHelpModeContextValue = {
  helpMode: boolean;
  enterHelpMode: () => void;
  exitHelpMode: () => void;
  toggleHelpMode: () => void;
};

const McpHelpModeContext = createContext<McpHelpModeContextValue | null>(null);

export const McpHelpModeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [helpMode, setHelpMode] = useState(false);

  const enterHelpMode = useCallback(() => setHelpMode(true), []);
  const exitHelpMode = useCallback(() => setHelpMode(false), []);
  const toggleHelpMode = useCallback(() => setHelpMode(prev => !prev), []);

  const value = useMemo<McpHelpModeContextValue>(
    () => ({ helpMode, enterHelpMode, exitHelpMode, toggleHelpMode }),
    [helpMode, enterHelpMode, exitHelpMode, toggleHelpMode]
  );

  return (
    <McpHelpModeContext.Provider value={value}>
      {children}
    </McpHelpModeContext.Provider>
  );
};

export function useMcpHelpMode(): McpHelpModeContextValue {
  const ctx = useContext(McpHelpModeContext);
  if (!ctx) {
    throw new Error('useMcpHelpMode must be used within McpHelpModeProvider');
  }
  return ctx;
}

export function useOptionalMcpHelpMode(): McpHelpModeContextValue | null {
  return useContext(McpHelpModeContext);
}
